import { memo, Dispatch, SetStateAction, useEffect } from 'react';

import { NextAction } from '../actionList.types';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from '@/shadcn/accordion';
import { cn } from '@/shadcn/lib/utils';
import { useMotionValue } from 'motion/react';
import { RadioGroup, RadioGroupItem } from '@/shadcn/radio-group';
import { Label } from '@/shadcn/label';
import { AvatarComponent } from '@/components/ui/avatar/avatar';

export interface PrioritySettings {
  priorityGroupLabel: string;
  priorityActionKey: string[];
}

export interface Link {
  groupKey: string;
  actionKey: string;
}

interface GroupActionsListProps {
  groupActions: NextAction;
  selectedLink: Partial<Link>;
  setSelectedLink: Dispatch<SetStateAction<Partial<Link>>>;
  priority?: PrioritySettings;
}

export const GroupActionsList = memo<GroupActionsListProps>(
  ({ groupActions, selectedLink, setSelectedLink, priority }) => {
    const test = useMotionValue('');

    useEffect(() => {
      test.set('only-item');
    }, []);

    // check if one of the actions is currently selected
    const selectedGroupAction =
      selectedLink?.groupKey === groupActions?.group?.label
        ? groupActions.actions.find(
            action => selectedLink.actionKey === action.key
          )
        : undefined;

    return (
      <Accordion
        type="single"
        collapsible
        defaultValue="only-item"
        data-cy="groupHeader"
      >
        <AccordionItem value="only-item" className="w-full py-2">
          <AccordionTrigger className="w-full cursor-pointer items-center px-2 py-2">
            <section className="flex w-full flex-row items-center justify-between gap-2">
              <div className="flex shrink flex-row items-center">
                <div className="mr-[15px]">
                  <AvatarComponent
                    size={35}
                    source={groupActions.group.avatar}
                    name={groupActions.group.name}
                  />
                </div>
                <div className="text-foreground/60 w-[300px] truncate text-sm font-semibold">
                  {`As ${groupActions.group.name}`}
                </div>
              </div>
              <div className="text-foreground/60 flex shrink-0 flex-row items-center justify-end text-xs text-nowrap">
                {`${groupActions.actions.length} action${
                  groupActions.actions.length > 1 ? 's' : ''
                }`}
              </div>
            </section>
          </AccordionTrigger>
          <AccordionContent className="ml-[55px] pb-2">
            <RadioGroup
              onValueChange={value => {
                setSelectedLink({
                  groupKey: groupActions.group.label,
                  actionKey: value
                });
              }}
              className="flex flex-col gap-0"
            >
              {groupActions.actions.map(action => {
                const uniqueKey = `${groupActions.group.label}-${action.key}`;
                const isPriority =
                  priority &&
                  groupActions.group.label === priority.priorityGroupLabel &&
                  priority.priorityActionKey.includes(action.key);
                return (
                  <div
                    key={uniqueKey}
                    className="text-foreground/60 hover:text-foreground/90 flex h-7 cursor-pointer flex-row items-center gap-2"
                  >
                    <RadioGroupItem
                      id={uniqueKey}
                      value={action.key}
                      checked={selectedGroupAction?.key === action.key}
                      data-cy={`next-action-${uniqueKey}`}
                    />
                    <Label
                      htmlFor={uniqueKey}
                      className={cn(
                        'flex h-full grow cursor-pointer items-center truncate pt-0.5',
                        isPriority ? 'font-bold' : 'font-medium'
                      )}
                    >
                      {action.title ?? action.key}
                    </Label>
                  </div>
                );
              })}
            </RadioGroup>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    );
  }
);
